import CommissionFee from "./commission-fee.vue";
import FeesLoader from "./fees-loader.vue";
import FeeLoader from "./fee-loader.vue";
import { computed, defineComponent, ref, toRefs, watch } from "@vue/composition-api";
import { useI18n } from "vue-i18n-composable";
import * as LenderSubmitActions from "@Broker/@Modules/clients/applications/application/store/lender-submit-actions";
import * as LenderSubmitGetters from "@Broker/@Modules/clients/applications/application/store/lender-submit-getters";
import * as ApplicationGetters from "@Broker/@Modules/clients/applications/application/store/application-getters";
import { useApplicationStoreGetters, useApplicationStoreActions } from "@Broker/@Modules/clients/applications/application/store/application-helpers";
import { debounce } from "lodash";
export default defineComponent({
  name: "FeesContainer",
  components: {
    FeesLoader,
    FeeLoader,
    CommissionFee
  },
  props: {
    applicationId: {
      type: [String, Number],
      required: true
    },
    clientUsername: {
      type: String,
      required: true
    },
    currentCommissionsMortgageType: {
      type: String,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const {
      applicationId,
      clientUsername,
      currentCommissionsMortgageType,
      isReadOnly
    } = toRefs(props);
    const loading = ref(false);
    const savingFee = ref("");
    const addingFee = ref(false);
    const {
      CURRENT_COMMISSIONS_FEES,
      MORTGAGE_AMOUNT
    } = useApplicationStoreGetters([LenderSubmitGetters.CURRENT_COMMISSIONS_FEES, ApplicationGetters.MORTGAGE_AMOUNT]);
    const {
      LOAD_COMMISSIONS_FEES,
      ADD_NEW_COMMISSION_FEE,
      DELETE_COMMISSION_FEE,
      CHANGE_COMMISSION_FEE
    } = useApplicationStoreActions([LenderSubmitActions.LOAD_COMMISSIONS_FEES, LenderSubmitActions.ADD_NEW_COMMISSION_FEE, LenderSubmitActions.DELETE_COMMISSION_FEE, LenderSubmitActions.CHANGE_COMMISSION_FEE]);
    const commissionsFees = computed(() => CURRENT_COMMISSIONS_FEES.value);
    const anyFeeWithOther = computed(() => {
      var _commissionsFees$valu;
      return (_commissionsFees$valu = commissionsFees.value) == null ? void 0 : _commissionsFees$valu.some(fee => fee.type === "other");
    });
    const {
      t
    } = useI18n();
    const columnHeaders = computed(() => [{
      label: t("direct-to-lender.commissions.fees.feeName"),
      shortLabel: "",
      size: "col-3 hidden-heading"
    }, {
      label: t("direct-to-lender.commissions.fees.basisPoints"),
      shortLabel: "",
      size: "col-1"
    }, {
      label: t("direct-to-lender.commissions.fees.amount"),
      shortLabel: "",
      size: "col-2"
    }, {
      label: t("direct-to-lender.commissions.fees.fundsReceived"),
      shortLabel: "",
      size: "col-3"
    }]);
    const loadCommissionsFees = async () => {
      loading.value = true;
      await LOAD_COMMISSIONS_FEES({
        applicationId: applicationId.value,
        clientUsername: clientUsername.value,
        mortgageType: currentCommissionsMortgageType.value
      });
      loading.value = false;
    };
    const handleAddFee = async () => {
      addingFee.value = true;
      await ADD_NEW_COMMISSION_FEE({
        applicationId: applicationId.value,
        clientUsername: clientUsername.value,
        mortgageType: currentCommissionsMortgageType.value
      });
      addingFee.value = false;
    };
    const handleRemoveFee = commissionsFeeId => {
      DELETE_COMMISSION_FEE({
        applicationId: applicationId.value,
        clientUsername: clientUsername.value,
        commissionsFeeId,
        mortgageType: currentCommissionsMortgageType.value
      });
    };
    const handleChange = debounce(async function (commissionFee) {
      if (!isReadOnly.value) {
        savingFee.value = commissionFee.commissionsFeeId;
        await CHANGE_COMMISSION_FEE({
          applicationId: applicationId.value,
          clientUsername: clientUsername.value,
          commissionFee,
          mortgageType: currentCommissionsMortgageType.value
        });
        savingFee.value = "";
      }
    }, 500);
    const fundedAmount = computed(() => MORTGAGE_AMOUNT.value(currentCommissionsMortgageType.value));
    const currentFeeIsSaving = ({
      commissionsFeeId
    }) => {
      return savingFee.value === commissionsFeeId;
    };
    watch(currentCommissionsMortgageType, value => {
      if (value) loadCommissionsFees();
    }, {
      immediate: true
    });
    return {
      columnHeaders,
      handleAddFee,
      handleRemoveFee,
      handleChange,
      commissionsFees,
      fundedAmount,
      loading,
      addingFee,
      savingFee,
      currentFeeIsSaving,
      anyFeeWithOther
    };
  }
});